import { useAuth } from '@contexts/auth-provider';
import { isAxiosError } from 'axios';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
    EtcaRedirectState,
    UseEtcaRedirectReturn,
} from './useEtcaRedirect.types';

import { useEtcaNavigation } from '../use-etca-navigation/useEtcaNavigation';
import { useEtcaRedirectQuery } from '../use-etca-redirect-query/useEtcaRedirectQuery';
import { parseEtcaRedirectParams } from './useEtcaRedirect.utils';


export function useEtcaRedirect(): UseEtcaRedirectReturn {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const [searchParams] = useSearchParams();

    const params = isAuthenticated ? parseEtcaRedirectParams(searchParams) : null;

    const redirectQuery = useEtcaRedirectQuery(params?.geminiReservationId ?? '');

    useEtcaNavigation(redirectQuery.data);

    const state = useMemo((): EtcaRedirectState => {
        if (isAuthLoading || !isAuthenticated) return { step: 'loading' };

        if (!params) return { step: 'invalid-params' };

        if (redirectQuery.isError) {
            if (
                isAxiosError(redirectQuery.error) &&
                redirectQuery.error.response?.status === 404
            ) {
                return { step: 'not-found' };
            }
            return { step: 'load-error' };
        }

        return { step: 'loading' };
    }, [
        isAuthLoading,
        isAuthenticated,
        params,
        redirectQuery.isError,
        redirectQuery.error,
    ]);

    const retry = useCallback((): void => {
        if (redirectQuery.isError) redirectQuery.refetch();
    }, [redirectQuery]);

    return { state, retry };
}
